import { handleServiceCall, ServiceResponse } from "@services"
import axios from "@plugins/axios.plugin"

type CreateCSTBRecordDto = {
    username: string
    firstname: string
    lastname: string
    phone: string
    buildings: [
        {
            name: string
            address: object
            sources: { idtype: string; idsource: string }[]
        },
    ]
}

type CreateBuildingDto = {
    name: string
    address: object
    sources: { idtype: string; idsource: string }[]
}

type UpdateBuildingDto = {
    name: string
    address: object
    info_logement: any
}

export class WattWatchersService {
    // ========================================================
    // Dossier CSTB
    // ========================================================

    /**
     * Récupération d'un dossier CSTB par son ID
     * @param id - L'ID de l'enregistrement CSTB
     * @returns Le dossier CSTB trouvé ou null sinon
     */
    async getCSTBRecordById(
        id: string
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]> {
        return handleServiceCall<ServiceResponse["GetCSTBRecordByIdResponse"]>(
            async () => {
                const {
                    data: { data },
                } = await axios.get(`/api/wattwatchers/cstb-record/${id}`)
                return data
            }
        )
    }
    /**
     * Ajoute un dossier CSTB
     * @param data - Données du dossier CSTB
     * @param data.username - Nom d'utilisateur
     * @param data.firstname - Prénom
     * @param data.lastname - Nom
     * @param data.username - Email
     * @param data.phone - Numéro de téléphone
     * @param data.buildings - Liste des bâtiments
     * @param data.buildings.name - Nom du bâtiment
     * @param data.buildings.address - Adresse du bâtiment
     * @param data.buildings.sources - Liste des sources
     * @param data.buildings.sources.idtype - Type de source
     * @param data.buildings.sources.idsource - Identifiant de la source
     * @returns La liste des adresses trouvées
     */
    async createCSTBRecord(
        data: CreateCSTBRecordDto
    ): Promise<ServiceResponse["PostCSTBRecordResponse"]> {
        return handleServiceCall<ServiceResponse["PostCSTBRecordResponse"]>(
            async () => {
                const {
                    data: { data: cstbRecord },
                } = await axios.post(`/api/wattwatchers/cstb-record`, data)
                return cstbRecord
            }
        )
    }

    // ========================================================
    // Bâtiment
    // ========================================================

    /**
     * Met à jour les informations d'un bâtiment d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param payload - Les données à mettre à jour
     * @param payload.name - Le nom du bâtiment
     * @param payload.address - L'adresse du bâtiment
     * @param payload.info_logement - Les informations sur le logement
     * @returns Le dossier CSTB mis à jour
     */
    /**
     * Balise de progression du formulaire (DEV-1061) — fire-and-forget :
     * une balise perdue ne casse rien, la suivante rattrape. AUCUN await
     * bloquant côté appelant, aucune erreur remontée à l'UI.
     */
    async postFormProgress(_id_cstb_record: string, step: string) {
        try {
            await axios.post(
                `/api/wattwatchers/cstb-record/${_id_cstb_record}/form-progress`,
                { step }
            )
        } catch {
            // silencieux par design : la télémétrie ne doit jamais gêner le ménage
        }
    }

    async patchBuilding(
        _id_cstb_record: string,
        _id_building: string,
        payload: Partial<UpdateBuildingDto>
    ) {
        return handleServiceCall<ServiceResponse["GetCSTBRecordByIdResponse"]>(
            async () => {
                const {
                    data: { data: cstbRecord },
                } = await axios.patch(
                    `/api/wattwatchers/cstb-record/${_id_cstb_record}/building/${_id_building}`,
                    payload
                )
                return cstbRecord
            }
        )
    }

    /**
     * Supprime un bâtiment d'un dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @returns Le dossier CSTB mis à jour
     */
    async deleteBuilding(
        _id_building: string,
        _id_cstb_record: string
    ): Promise<void> {
        return handleServiceCall<void>(async () => {
            await axios.delete(
                `/api/wattwatchers/cstb-record/${_id_cstb_record}/building/${_id_building}`
            )
        })
    }

    /**
     * Ajoute un bâtiment à un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param payload - Les données du bâtiment à ajouter
     * @returns Le dossier CSTB mis à jour
     */
    async addBuilding(
        _id_cstb_record: string,
        payload: CreateBuildingDto
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]> {
        return handleServiceCall<ServiceResponse["GetCSTBRecordByIdResponse"]>(
            async () => {
                const {
                    data: { data: cstbRecord },
                } = await axios.post(
                    `/api/wattwatchers/cstb-record/${_id_cstb_record}/building`,
                    payload
                )
                return cstbRecord
            }
        )
    }

    /**
     * Permet de signer le consentement d'un bâtiment d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param options.text - Texte affiché et coché (module `consent-texts`)
     * @param [options.consent_checked] - `true` quand la case a été cochée
     *
     * @returns Le dossier CSTB mis à jour
     */
    async signFostAndDpeConsent(
        _id_cstb_record: string,
        _id_building: string,
        { text, consent_checked }: { text: string; consent_checked?: boolean }
    ) {
        return handleServiceCall<ServiceResponse["GetCSTBRecordByIdResponse"]>(
            async () => {
                // `text` : la phrase réellement affichée et cochée, produite par
                // le module de textes (DEV-2274). `consent_checked` est un champ
                // additif : une api-ww qui ne le connaît pas l'ignore.
                const body: { text: string; consent_checked?: true } = { text }
                if (consent_checked === true) body.consent_checked = true
                const {
                    data: { data: cstbRecord },
                } = await axios.post(
                    `/api/wattwatchers/cstb-record/${_id_cstb_record}/building/${_id_building}/sign`,
                    body
                )
                return cstbRecord
            }
        )
    }

    // ========================================================
    // Source
    // ========================================================

    /**
     * Lance une recherche de PDL pour un bâtiment d'un dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @returns Le dossier CSTB mis à jour
     */
    async searchPdlForBuilding(
        _id_building: string,
        _id_cstb_record: string
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]> {
        return handleServiceCall<ServiceResponse["GetCSTBRecordByIdResponse"]>(
            async () => {
                const {
                    data: { data: cstbRecord },
                } = await axios.get(
                    `/api/wattwatchers/cstb-record/${_id_cstb_record}/building/${_id_building}/search_pdl`
                )
                return cstbRecord
            }
        )
    }

    /**
     * Ajoute une source à un bâtiment d'un dossier CSTB sans la signer
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param idtype - Le type de la source
     * @param idsource - L'ID réel de la source
     * @param date_from_data - Date de début du contrat (pour PCE uniquement)
     * @returns Le dossier CSTB mis à jour
     */
    async addSource(
        _id_cstb_record: string,
        _id_building: string,
        idtype: string,
        idsource: string,
        date_from_data?: string
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]> {
        return handleServiceCall<ServiceResponse["GetCSTBRecordByIdResponse"]>(
            async () => {
                const {
                    data: { data: cstbRecord },
                } = await axios.post(
                    `/api/wattwatchers/cstb-record/${_id_cstb_record}/building/${_id_building}/source`,
                    {
                        idtype,
                        idsource,
                        date_from_data,
                    }
                )
                return cstbRecord
            }
        )
    }

    /**
     * Permet de signer le consentement d'une source d'un bâtiment d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param idtype - Le type de la source
     * @param idsource - L'ID réel de la source
     * @param options - Options supplémentaires
     * @param [options.consent_checked] - `true` quand la case a été cochée (DEV-2274)
     * @returns Le dossier CSTB mis à jour
     */
    async signSource(
        _id_cstb_record: string,
        _id_building: string,
        idtype: string,
        idsource: string,
        { consent_checked }: { consent_checked?: boolean } = {}
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]> {
        return handleServiceCall<ServiceResponse["GetCSTBRecordByIdResponse"]>(
            async () => {
                // Case cochée dans le formulaire (DEV-2274) : champ additif et
                // facultatif. Sans case, la requête reste exactement celle
                // d'avant (aucun corps).
                const {
                    data: { data: cstbRecord },
                } = await axios.post(
                    `/api/wattwatchers/cstb-record/${_id_cstb_record}/building/${_id_building}/source/sign/${idtype}/${idsource}`,
                    consent_checked === true
                        ? { consent_checked: true }
                        : undefined
                )
                return cstbRecord
            }
        )
    }

    /**
     * Suppression d'une source d'un bâtiment d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param _id_building - L'ID du bâtiment
     * @param idtype - Le type de la source
     * @param idsource - L'ID réel de la source
     * @returns Le dossier CSTB mis à jour
     */
    async deleteSource(
        _id_cstb_record: string,
        _id_building: string,
        idtype: string,
        idsource: string
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]> {
        return handleServiceCall<ServiceResponse["GetCSTBRecordByIdResponse"]>(
            async () => {
                const {
                    data: { data: cstbRecord },
                } = await axios.delete(
                    `/api/wattwatchers/cstb-record/${_id_cstb_record}/building/${_id_building}/source/type/${idtype}/${idsource}`
                )
                return cstbRecord
            }
        )
    }

    /**
     * Met à jour les informations utilisateur d'un dossier CSTB
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @param payload - Les données utilisateur à mettre à jour
     * @param payload.firstname - Le prénom de l'utilisateur
     * @param payload.lastname - Le nom de l'utilisateur
     * @param payload.phone - Le numéro de téléphone de l'utilisateur
     *
     * @returns Le dossier CSTB mis à jour
     */
    async updateCSTBRecordUser(
        _id_cstb_record: string,
        payload: Partial<
            Record<"firstname" | "lastname" | "username" | "phone", string>
        >
    ): Promise<ServiceResponse["GetCSTBRecordByIdResponse"]> {
        return handleServiceCall<ServiceResponse["GetCSTBRecordByIdResponse"]>(
            async () => {
                const {
                    data: { data: cstbRecord },
                } = await axios.patch(
                    `/api/wattwatchers/cstb-record/${_id_cstb_record}/user`,
                    payload
                )
                return cstbRecord
            }
        )
    }

    /**
     * Envoie d'un mail de fin quand le dossier CSTB est terminé
     * @param _id_cstb_record - L'ID du dossier CSTB
     * @returns
     */
    async sendFinalMail(_id_cstb_record: string) {
        return handleServiceCall<void>(async () => {
            await axios.post(
                `/api/wattwatchers/cstb-record/${_id_cstb_record}/mail`,
                {
                    type: "final",
                }
            )
        })
    }

    /**
     * Se désinscrire des mails de suivi d'un dossier CSTB
     */
    async unsubscribeMails(username: string) {
        return handleServiceCall<void>(async () => {
            await axios.post(`/api/wattwatchers/mail/unsubscribe`, {
                username,
            })
        })
    }
}

export type { CreateCSTBRecordDto }
